// src/Ontology/parallel.js

import React, { useRef, useEffect, useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import './parallel.css';

export default function ParallelTimeline({
  nodes = [
    { id: 'root', label: 'Event Node', parent: null, depth: 0 },
    { id: 'A',    label: 'A',     parent: 'root', depth: 1 },
    { id: 'B',    label: 'B',     parent: 'root', depth: 1 },
    { id: 'A1',   label: 'A1',    parent: 'A',    depth: 2 },
    { id: 'A2',   label: 'A2',    parent: 'A',    depth: 2 },
    { id: 'B1',   label: 'B1',    parent: 'B',    depth: 2 },
    { id: 'B2',   label: 'B2',    parent: 'B',    depth: 2 },
  ],
  onSelect = id => console.log('Selected', id)
}) {
  const ref = useRef(null);
  const [size, setSize] = useState({ w: window.innerWidth, h: 50 });
  const padding = 60;

  // 容器自适应
  useEffect(() => {
    const update = () => {
      if (ref.current) {
        setSize({
          w: ref.current.clientWidth,
          h: ref.current.clientHeight
        });
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const { w, h } = size;
  const innerW = w - padding * 2;
  const maxDepth = Math.max(...nodes.map(n => n.depth));

  // 分层数据
  const layers = useMemo(() => {
    const m = {};
    nodes.forEach(n => {
      m[n.depth] = m[n.depth] || [];
      m[n.depth].push(n);
    });
    return m;
  }, [nodes]);

  const nodeMap = useMemo(
    () => Object.fromEntries(nodes.map(n => [n.id, n])),
    [nodes]
  );

  // 构造 SVG 结构
  const timeline = (
    <div className="parallel-container" ref={ref}>
      <svg width={w} height={h}>
        {/* 连线 */}
        {nodes.map(n => {
          if (!n.parent) return null;
          const p = nodeMap[n.parent];
          const x1 = padding + (p.depth / maxDepth) * innerW;
          const x2 = padding + (n.depth / maxDepth) * innerW;

          const sib1 = layers[p.depth];
          const y1 = ((sib1.findIndex(x => x.id === p.id) + 1) / (sib1.length + 1)) * h;

          const sib2 = layers[n.depth];
          const y2 = ((sib2.findIndex(x => x.id === n.id) + 1) / (sib2.length + 1)) * h;

          const cx = (x1 + x2) / 2;
          return (
            <path
              key={n.id}
              className="branch-link"
              d={`M${x1},${y1} C${cx},${y1} ${cx},${y2} ${x2},${y2}`}
            />
          );
        })}

        {/* 节点 */}
        {nodes.map(n => {
          const x = padding + (n.depth / maxDepth) * innerW;
          const sib = layers[n.depth];
          const y = ((sib.findIndex(x => x.id === n.id) + 1) / (sib.length + 1)) * h;

          // 最右层标签左对齐
          const isLast = n.depth === maxDepth;
          const textX = isLast ? -8 : 8;
          const anchor = isLast ? 'end' : 'start';

          return (
            <g key={n.id} transform={`translate(${x},${y})`}>
              <g className="branch-node" onClick={() => onSelect(n.id)}>
                <circle r="6" />
                <text x={textX} y="4" textAnchor={anchor}>
                  {n.label}
                </text>
              </g>
            </g>
          );
        })}
      </svg>
    </div>
  );

  // Portal 到 body，脱离 Angel4 的 overflow:hidden
  return ReactDOM.createPortal(timeline, document.body);
}
